import {create, createMany, path, validate, expr, alias, incomplete, index, middleware, on} from '@acng/frontend-voyager';
import {replaceUrl} from '@acng/frontend-relativity/history';
import {CTX_PROVIDE} from '@acng/frontend-relativity/minify';
import {once} from '@acng/frontend-bounty/dom/event.js';
import {setTimeout} from '@acng/frontend-bounty/dom/window.js';

import {hookFeature} from 'acng/core/config/feature';
import {main} from '../../core/service/location.js';
import {userRoute, guestRoute} from 'acng/userPool/config/routes';
import {mainView, stickyView} from 'acng/layout/service/sequence.js';
import {authUser} from '../../userPool/context/auth-user.js';
import {ProfileVisit} from '../../Lib/Log/events/visit.js';
import {logger} from '../../Lib/Log/client.js';

import {ctxAmateur} from '../context/amateur.js';
import {userVisitsAmateur} from 'acng/amateurPool/service/profile-visit.js';
import {amateurPoolFeature} from './feature';
import {getByNickname} from '../service/cache.js';
import {trackOncePerMinute} from '../../userPool/service/activity.js';

/** @typedef {import('acng/userPool/factory/user').User} User */

export const amateurRoute = main.create(
  path('/sedcard/{nickname}'),
  incomplete,
  alias('sedcardNav'),
  createMany(
    [
      index,
      incomplete,
      mainView(amateurPoolFeature, 'sedcard'),
      alias('sedcard'), //
      on('active', () => {
        if (!main.globals.amateur) {
          console.log('ACTIVE SEDCARD amateur not found!!', main.globals.nickname);
          replaceUrl('/#/');
        }
      }),
    ],
  )
);

main.create(
  path('/sedcard'),
  stickyView(amateurPoolFeature, 'sedcard-header'),
  incomplete
);

const sedcardIndex = amateurRoute.create(
  index,
  on('enter', () => {
    const amateur = /** @type {?Amateur} */ (sedcardIndex.params.amateur);
    if (!authUser || !amateur) return;
    DEBUG: console.debug('laforge profile', main.globals.amateur);
    const laforgeEvent = new ProfileVisit(authUser.uid, amateur);
    once(sedcardIndex, 'leave', () => logger.send(laforgeEvent.endVisit()));
  })
);

userRoute.createMany(
  [
    incomplete,
    path('/amateurs'),
    createMany(
      [
        incomplete,
        expr(/(?:online|top|new|gameScore)$/),
        stickyView(amateurPoolFeature, 'list-header'),
      ],
      [
        path('/{amateurs}'),
        validate(({amateurs}) => /^(online|top|new|gameScore)$/.test(/** @type {string} */ (amateurs))),
        mainView(amateurPoolFeature, 'list'),
      ],
      [
        path('/livedating'),
        mainView(amateurPoolFeature, 'list-livedating'),
      ]
    )
  ],
  [path('/visits'), alias('profileVisits')],
  [path('/amateurs/search'), mainView(hookFeature, 'amateurSearch'), alias('amateurSearch')],
  [
    incomplete,
    path('/feed'),
    stickyView(amateurPoolFeature, 'feed-header'),
    create(
      path('/{feed}'),
      validate(({feed}) => /^(all|posts|media|favorites)$/.test(/** @type {string} */ (feed))),
      mainView(amateurPoolFeature, 'feed'),
    )
  ]
);

guestRoute.create(
  path('/amateursignup'), //
  alias('amateursignup')
);

ctxAmateur[CTX_PROVIDE](null, null);

/**
 * @typedef RootScope
 * @property {boolean} floatingMenu -
 */
runSedcardNav.$inject = ['user'];
/**
 * @param {User} user -
 */
export function runSedcardNav(user) {
  amateurRoute.config(
    on('change', event => {
      const amateur = /** @type {CustomEvent} */ (event).detail.route.params.amateur ?? null;
      DEBUG: console.info('produce', amateur);
      ctxAmateur[CTX_PROVIDE](null, amateur);
    }),
    on('enter', ev => {
      const amateur = /** @type {Amateur | undefined} */ (amateurRoute.params.amateur);
      if (user.guest || !amateur) return;
      DEBUG: amateurRoute.VERBOSE && console.debug('AmateurVisit triggered by Route-Event %O', ev);
      userVisitsAmateur(amateur.id);
    }),
    on('nochange', trackOncePerMinute),
    middleware((next, prev) => {
      if (next.nickname == prev.nickname) {
        next.amateur = prev.amateur;
        return true;
      }

      const nextNickname = /** @type {string} */ (next.nickname);
      DEBUG: amateurRoute.VERBOSE && console.debug('amateurPool/config/routes runSedcardNav', {nextNickname});
      return getByNickname(nextNickname)
        .then((amateur) => next.amateur = amateur)
        .catch(reason => {
          console.warn(`sedcardNav Amateur '${nextNickname}' rejected with reason`, reason);
        });
    })
  );
}
